import { api } from '../../../api/Middleware';
import { GAMIFICATION_URL } from '../../../api/Urls';
import Swal from 'sweetalert2';

export const GamificationServices = {

  async getDashboard() {
    try {
      const response = await api.get(`${GAMIFICATION_URL}dashboard/`);
      if (response.status === 200) {
        return {
          success: true,
          data: response.data
        };
      }
    } catch (error) {
      console.error('Error fetching gamification dashboard:', error);
      
      if (error.response?.status === 403) {
        return {
          success: false,
          error: 'not_eligible',
          message: 'Usuário não elegível para gamificação'
        };
      }
      
      return {
        success: false,
        error: 'api_error',
        message: error.response?.data?.message || 'Erro ao carregar dashboard de gamificação'
      };
    }
  },

  async getProfile() {
    try {
      const response = await api.get(`${GAMIFICATION_URL}profile/`);
      if (response.status === 200) {
        return {
          success: true,
          data: response.data
        };
      }
    } catch (error) {
      console.error('Error fetching gamification profile:', error);
      return {
        success: false,
        error: 'api_error',
        message: error.response?.data?.message || 'Erro ao carregar perfil de gamificação'
      };
    }
  },

  async getLeaderboard(params = {}) {
    try {
      const { limit = 50, module } = params;
      let url = `${GAMIFICATION_URL}leaderboard/?limit=${limit}`;
      
      if (module) {
        url += `&module=${module}`;
      }
      
      const response = await api.get(url);
      if (response.status === 200) {
        return {
          success: true,
          data: response.data
        };
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      return {
        success: false,
        error: 'api_error',
        message: error.response?.data?.message || 'Erro ao carregar ranking'
      };
    }
  },

  async getAchievements(includeLocked = true) {
    try {
      const response = await api.get(
        `${GAMIFICATION_URL}achievements/?include_locked=${includeLocked}`
      );
      if (response.status === 200) {
        return {
          success: true,
          data: response.data
        };
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
      return {
        success: false,
        error: 'api_error',
        message: error.response?.data?.message || 'Erro ao carregar conquistas'
      };
    }
  },

  async getXpHistory(params = {}) {
    try {
      const { module, days = 30 } = params;
      let url = `${GAMIFICATION_URL}xp_history/?days=${days}`;
      
      if (module) {
        url += `&module=${module}`;
      }
      
      const response = await api.get(url);
      if (response.status === 200) {
        return {
          success: true,
          data: response.data
        };
      }
    } catch (error) {
      console.error('Error fetching XP history:', error);
      return {
        success: false,
        error: 'api_error',
        message: error.response?.data?.message || 'Erro ao carregar histórico de XP'
      };
    }
  },

  async getDailyStats(date) {
    try {
      let url = `${GAMIFICATION_URL}daily_stats/`;
      if (date) {
        url += `?date=${date}`;
      }
      
      const response = await api.get(url);
      if (response.status === 200) {
        return {
          success: true,
          data: response.data
        };
      }
    } catch (error) {
      console.error('Error fetching daily stats:', error);
      return {
        success: false,
        error: 'api_error',
        message: error.response?.data?.message || 'Erro ao carregar estatísticas diárias'
      };
    }
  },

  async getLevels() {
    try {
      const response = await api.get(`${GAMIFICATION_URL}levels/`);
      if (response.status === 200) {
        return {
          success: true,
          data: response.data
        };
      }
    } catch (error) {
      console.error('Error fetching levels:', error);
      return {
        success: false,
        error: 'api_error',
        message: error.response?.data?.message || 'Erro ao carregar informações de níveis'
      };
    }
  },

  async getXpActions(module) {
    try {
      let url = `${GAMIFICATION_URL}xp_actions/`;
      if (module) {
        url += `?module=${module}`;
      }
      
      const response = await api.get(url);
      if (response.status === 200) {
        return {
          success: true,
          data: response.data
        };
      }
    } catch (error) {
      console.error('Error fetching XP actions:', error);
      return {
        success: false,
        error: 'api_error',
        message: error.response?.data?.message || 'Erro ao carregar ações de XP'
      };
    }
  },

  showErrorModal(error) {
    let title = 'Erro';
    let message = 'Ocorreu um erro inesperado';
    
    switch (error.error) {
      case 'not_eligible':
        title = 'Acesso Restrito';
        message = 'O sistema de gamificação está disponível apenas para planos Premium (Growth, Scale, Pro e Founder).';
        break;
      case 'api_error':
        title = 'Erro de Conexão';
        message = error.message || 'Erro ao conectar com o servidor';
        break;
      default:
        message = error.message || message;
    }

    Swal.fire({
      icon: 'error',
      title: title,
      text: message,
      confirmButtonText: 'Ok',
      confirmButtonColor: 'var(--primary)'
    });
  },

  showLevelUpModal(levelData) {
    const { new_level, tier_info, level_benefits, xp_earned } = levelData;
    
    Swal.fire({
      icon: 'success',
      title: '🎉 Parabéns!',
      html: `
        <div style="text-align: center; margin: 20px 0;">
          <h3 style="color: var(--primary); margin-bottom: 10px;">
            Você subiu para o <strong>Nível ${new_level}</strong>!
          </h3>
          <div style="display: flex; align-items: center; justify-content: center; margin: 15px 0;">
            <span style="font-size: 2rem; margin-right: 10px;">${this.getTierEmoji(tier_info.tier)}</span>
            <div>
              <div style="font-weight: bold; color: var(--success);">${tier_info.name}</div>
              <div style="color: var(--text-muted); font-size: 0.9rem;">${tier_info.description}</div>
            </div>
          </div>
          <div style="background: var(--surface); padding: 15px; border-radius: 8px; margin: 15px 0;">
            <div style="font-weight: bold; margin-bottom: 5px;">+${xp_earned} XP conquistado!</div>
            <div style="color: var(--text-muted); font-size: 0.9rem;">
              Continue completando atividades para subir mais níveis
            </div>
          </div>
        </div>
      `,
      showCancelButton: false,
      confirmButtonText: 'Fantástico!',
      confirmButtonColor: 'var(--success)',
      showClass: {
        popup: 'animate__animated animate__bounceIn'
      },
      hideClass: {
        popup: 'animate__animated animate__bounceOut'
      }
    });
  },

  getTierEmoji(tier) {
    const tierEmojis = {
      bronze: '🥉',
      silver: '🥈', 
      gold: '🥇',
      diamond: '💎'
    };
    return tierEmojis[tier] || '⭐';
  },

  getTierColor(tier) {
    const tierColors = {
      bronze: '#CD7F32',
      silver: '#C0C0C0',
      gold: '#FFD700',
      diamond: '#B9F2FF'
    };
    return tierColors[tier] || 'var(--primary)';
  },

  formatXP(xp) {
    if (xp >= 1000000) {
      return `${(xp / 1000000).toFixed(1)}M`;
    } else if (xp >= 1000) {
      return `${(xp / 1000).toFixed(1)}K`;
    }
    return xp.toString();
  },

  calculateTimeToNextLevel(currentXP, neededXP) {
    const remainingXP = neededXP - currentXP;
    const avgDailyXP = 150; // Estimativa baseada na atividade média
    const daysToNext = Math.ceil(remainingXP / avgDailyXP);
    
    if (daysToNext <= 7) {
      return `~${daysToNext} dia${daysToNext > 1 ? 's' : ''}`;
    } else if (daysToNext <= 30) {
      const weeks = Math.ceil(daysToNext / 7);
      return `~${weeks} semana${weeks > 1 ? 's' : ''}`;
    } else {
      const months = Math.ceil(daysToNext / 30);
      return `~${months} mês${months > 1 ? 'es' : ''}`;
    }
  }
};

export default GamificationServices;